import { APP_RUNTIME_LAYOUTS } from "../Runtime/appRuntime.generated";

const getConfig = () => window.__APP_CONFIG__ || {};

const getCompanyBranding = () => {
  const config = getConfig();

  return {
    logoDark: config.APP_LOGO_DARK || "", logoLight: config.APP_LOGO_LIGHT || "", headerImage: "",
    faviconIco: config.APP_FAVICON_ICO || "", favicon32: config.APP_FAVICON_32 || "", favicon16: config.APP_FAVICON_16 || "",
    appleIcon: config.APP_FAVICON_APPLE || "", icon192: config.APP_ICON_192 || "", icon512: config.APP_ICON_512 || "",
    ogImage: config.APP_OG_IMAGE_URL || "", twitterImage: config.APP_TWITTER_IMAGE_URL || "", themeColor: config.APP_THEME_COLOR || "#111111",
  };
};

const setHref = (id, value) => {
  const element = document.getElementById(id);
  if (element && value) element.setAttribute("href", value);
};

const setMeta = (selector, value) => {
  const element = document.querySelector(selector);
  if (element && value) element.setAttribute("content", value);
};

const normalizeBranding = (payload = {}, fallback = getCompanyBranding()) => {
  const assets = payload.assets || payload;

  return {
    logoDark: assets.logoDark || fallback.logoDark, logoLight: assets.logoLight || fallback.logoLight,
    headerImage: assets.headerImage || fallback.headerImage || "", faviconIco: assets.faviconIco || fallback.faviconIco,
    favicon32: assets.favicon32 || fallback.favicon32, favicon16: assets.favicon16 || fallback.favicon16,
    appleIcon: assets.appleIcon || fallback.appleIcon, icon192: assets.icon192 || fallback.icon192,
    icon512: assets.icon512 || fallback.icon512, ogImage: assets.ogImage || fallback.ogImage,
    twitterImage: assets.twitterImage || assets.ogImage || fallback.twitterImage,
    themeColor: assets.themeColor || payload.themeColor || fallback.themeColor,
  };
};

const getLocalCelebrationFolder = (celebrationId) => {
  const config = getConfig();
  const base = String(config.APP_CELEBRATION_ASSETS_BASE_URL || "/_Brand/CelebrationDate").replace(/\/$/, "");
  const testFolder = APP_RUNTIME_LAYOUTS.IS_LOGO_TEST_CELEBRATION_DATE === true ? "/TEST" : "";
  return `${base}${testFolder}/${celebrationId}`;
};

const buildLocalBranding = (celebrationId) => {
  const folder = getLocalCelebrationFolder(celebrationId);

  return normalizeBranding({
    logoDark: `${folder}/logo-dark.png`, logoLight: `${folder}/logo-light.png`, headerImage: `${folder}/header.png`,
    faviconIco: `${folder}/FAVICON.ico`, favicon32: `${folder}/FAVICON_32.png`, favicon16: `${folder}/FAVICON_16.png`,
    appleIcon: `${folder}/FAVICON_APPLE_180.png`, icon192: `${folder}/logo192.png`, icon512: `${folder}/logo512.png`,
    ogImage: `${folder}/social-preview.png`, twitterImage: `${folder}/social-preview.png`,
  });
};

const buildApiUrl = (template, celebrationId) => {
  if (!template) return "";
  if (template.includes("{celebrationId}")) return template.replace("{celebrationId}", encodeURIComponent(celebrationId));
  return `${template.replace(/\/$/, "")}/${encodeURIComponent(celebrationId)}`;
};

let activeManifestUrl = "";

const updateManifest = (branding) => {
  const config = getConfig();
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) return;

  const manifest = {
    short_name: config.APP_SHORT_NAME || config.APP_NAME || "APP",
    name: config.APP_NAME || config.COMPANY_NAME || "APP",
    description: config.APP_DESCRIPTION || "",
    icons: [
      { src: branding.icon192, sizes: "192x192", type: "image/png", purpose: "any maskable" },
      { src: branding.icon512, sizes: "512x512", type: "image/png", purpose: "any maskable" },
    ],
    start_url: config.APP_PWA_START_URL || ".", scope: config.APP_PWA_SCOPE || ".",
    display: config.APP_PWA_DISPLAY || "standalone", orientation: config.APP_PWA_ORIENTATION || "any",
    lang: config.APP_LANGUAGE || "es", dir: config.APP_DIRECTION || "ltr",
    theme_color: branding.themeColor, background_color: config.APP_BACKGROUND_COLOR || branding.themeColor,
  };

  if (activeManifestUrl) URL.revokeObjectURL(activeManifestUrl);
  activeManifestUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }));
  manifestLink.setAttribute("href", activeManifestUrl);
};

export async function resolveCelebrationBranding(celebrationId) {
  const config = getConfig();

  if (APP_RUNTIME_LAYOUTS.IS_MODE_CELEBRATION_DATE !== true || !celebrationId) return getCompanyBranding();
  if (APP_RUNTIME_LAYOUTS.IS_LOGO_TEST_CELEBRATION_DATE === true) return buildLocalBranding(celebrationId);
  if (APP_RUNTIME_LAYOUTS.IS_LOGO_APP_DB_DYNAMIC !== true) return buildLocalBranding(celebrationId);

  const url = buildApiUrl(config.APP_CELEBRATION_BRANDING_API_URL, celebrationId);
  if (!url) return getCompanyBranding();

  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error(`No se pudo obtener el branding (${response.status}).`);

  return normalizeBranding(await response.json());
}

export function applyBrandingToDocument(branding) {
  const resolved = normalizeBranding(branding);

  setHref("app-favicon", resolved.faviconIco);
  setHref("app-favicon-32", resolved.favicon32);
  setHref("app-favicon-16", resolved.favicon16);
  setHref("app-apple-icon", resolved.appleIcon);
  setMeta('meta[name="theme-color"]', resolved.themeColor);
  setMeta('meta[property="og:image"]', resolved.ogImage);
  setMeta('meta[name="twitter:image"]', resolved.twitterImage);
  updateManifest(resolved);

  window.__ACTIVE_BRANDING__ = Object.freeze({ ...resolved });
  window.dispatchEvent(new CustomEvent("app-branding-change", { detail: resolved }));
  return resolved;
}

export async function applyCelebrationBranding(celebrationId) {
  return applyBrandingToDocument(await resolveCelebrationBranding(celebrationId));
}

export function applyUploadedBranding(files = {}) {
  const makeUrl = (file) => file ? URL.createObjectURL(file) : "";
  const fallback = getCompanyBranding();
  const logoUrl = makeUrl(files.logo);
  const faviconUrl = makeUrl(files.favicon);
  const headerUrl = makeUrl(files.headerImage);
  const socialUrl = makeUrl(files.socialImage);

  return applyBrandingToDocument({
    logoDark: logoUrl || fallback.logoDark, logoLight: logoUrl || fallback.logoLight, headerImage: headerUrl,
    faviconIco: faviconUrl || fallback.faviconIco, favicon32: faviconUrl || fallback.favicon32,
    favicon16: faviconUrl || fallback.favicon16, appleIcon: faviconUrl || fallback.appleIcon,
    icon192: faviconUrl || fallback.icon192, icon512: faviconUrl || fallback.icon512,
    ogImage: socialUrl || headerUrl || fallback.ogImage, twitterImage: socialUrl || headerUrl || fallback.twitterImage,
    themeColor: fallback.themeColor,
  });
}

export function restoreDefaultBranding() {
  return applyBrandingToDocument(getCompanyBranding());
}

export function getActiveBranding() {
  return window.__ACTIVE_BRANDING__ || getCompanyBranding();
}

export async function uploadCelebrationBranding(celebrationId, files = {}) {
  const config = getConfig();
  const url = config.APP_CELEBRATION_BRANDING_UPLOAD_URL || "";
  if (!url) throw new Error("APP_CELEBRATION_BRANDING_UPLOAD_URL no está configurado.");

  const body = new FormData();
  body.append("celebrationId", celebrationId);
  if (files.logo) body.append("logo", files.logo);
  if (files.favicon) body.append("favicon", files.favicon);
  if (files.headerImage) body.append("headerImage", files.headerImage);
  if (files.socialImage) body.append("socialImage", files.socialImage);

  const response = await fetch(url, { method: "POST", body, credentials: "include" });
  if (!response.ok) throw new Error(`No se pudo guardar el branding (${response.status}).`);
  return response.json();
}
